import React, { useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import LogoFogedomScic from '../components/LogoFogedomScic';
import { Download, Info, Shield, TrendingUp } from 'lucide-react';

const LogoFogedomPage = () => {
  const _svgRef = useRef(null);

  const handleDownloadSVG = () => {
    const svgElement = document.querySelector('#fogedom-logo svg');
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'FOGEDOM-SCIC-Logo.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPNG = () => {
    const svgElement = document.querySelector('#fogedom-logo svg');
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = 1200;
    canvas.height = 1200;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'FOGEDOM-SCIC-Logo.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackButton to="/" label="Retour à l'accueil" />
      </div>

      <div className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-indigo-900 mb-4">
              Logo FOGEDOM-SCIC
            </h1>
            <p className="text-lg text-gray-600 mb-2">
              Fonds de Garantie Solidaire DOM
            </p>
            <p className="text-sm text-gray-500">
              Fonds interne de la SCIC OBJECTIF SCOP OUTREMER
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Affichage du Logo */}
            <Card className="shadow-xl">
              <CardContent className="p-4 md:p-8">
                <h2 className="text-2xl font-bold text-indigo-900 mb-6 text-center">
                  Aperçu du Logo
                </h2>
                
                <div id="fogedom-logo" className="flex justify-center items-center bg-white p-8 rounded-lg border-2 border-gray-200 mb-6">
                  <LogoFogedomScic width={400} height={400} />
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleDownloadSVG}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                  >
                    <Download className="h-5 w-5" />
                    Télécharger en SVG
                  </button>
                  
                  <button
                    onClick={handleDownloadPNG}
                    className="w-full flex items-center justify-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors font-semibold"
                  >
                    <Download className="h-5 w-5" />
                    Télécharger en PNG (HD)
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Informations */}
            <Card className="shadow-xl">
              <CardContent className="p-4 md:p-8">
                <h2 className="text-2xl font-bold text-indigo-900 mb-6">
                  À propos du Logo
                </h2>

                <div className="space-y-6">
                  {/* Symbolique */}
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      Symbolique
                    </h3>
                    <p className="text-sm text-gray-700">
                      Le logo FOGEDOM-SCIC représente un fonds de garantie solidaire avec 4 pétales symbolisant les piliers du fonds :
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-700 ml-4">
                      <li>🟡 <strong>Or</strong> : Valeur et solidité du fonds</li>
                      <li>🟢 <strong>Vert</strong> : Garantie et sécurité</li>
                      <li>🔵 <strong>Bleu</strong> : Stabilité financière</li>
                      <li>🔷 <strong>Cyan</strong> : Innovation et modernité</li>
                    </ul>
                  </div>

                  {/* Centre */}
                  <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-600">
                    <h3 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Centre Protecteur
                    </h3>
                    <p className="text-sm text-gray-700">
                      Le centre du logo présente un bouclier avec les initiales "F.G" (Fonds de Garantie), 
                      symbolisant la protection et la sécurité apportées par FOGEDOM-SCIC.
                    </p>
                  </div>

                  {/* Usage */}
                  <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-600">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Usage
                    </h3>
                    <p className="text-sm text-gray-700">
                      Ce logo peut être utilisé pour :
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-700 ml-4">
                      <li>• Documents officiels FOGEDOM-SCIC</li>
                      <li>• Communications internes</li>
                      <li>• Rapports financiers</li>
                      <li>• Présentations institutionnelles</li>
                    </ul>
                  </div>

                  {/* Formats */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Formats Disponibles
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-gray-900">SVG</p>
                        <p className="text-xs text-gray-600">Vectoriel, qualité infinie</p>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-gray-900">PNG HD</p>
                        <p className="text-xs text-gray-600">1200x1200px, fond blanc</p>
                      </div>
                    </div>
                  </div>

                  {/* Variantes */}
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-600">
                    <h3 className="font-semibold text-green-900 mb-2">
                      Variantes de Taille
                    </h3>
                    <div className="flex items-center justify-around mt-3">
                      <LogoFogedomScic width={80} height={80} />
                      <LogoFogedomScic width={120} height={120} />
                      <LogoFogedomScic width={160} height={160} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Informations légales */}
          <Card className="shadow-xl mt-8">
            <CardContent className="p-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-bold text-indigo-900 mb-3 text-lg">
                  FOGEDOM-SCIC – Fonds Interne
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <p className="font-semibold mb-1">Structure :</p>
                    <p>Fonds interne de la SCIC SAS OBJECTIF SCOP OUTREMER</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Rôle :</p>
                    <p>Analyse technique, accompagnement solidaire, actions coopératives</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Statut :</p>
                    <p>Fonds de garantie solidaire (non assurantiel)</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Cadre :</p>
                    <p>Économie Sociale et Solidaire (ESS)</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-xs text-gray-600 italic">
                    ⚠️ FOGEDOM-SCIC n'est pas une assurance et ne fournit aucune prestation assurantielle.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default LogoFogedomPage;
